//login.js
import React, { useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import strobeLogoMono from "../assets/images/strobelogo.svg";
import loginImage from "../assets/images/loginimage.png";
import TopNavBar from "../components/TopNavBar";
import { Link } from "react-router-dom";
import { ChevronDown, Eye, EyeOff } from "lucide-react"; // 
import { trackClickEvent } from "../utils/analytics";
import { useEffect } from "react";
import { defaultRouteForAccess, getUserAccess } from "../utils/authRoles";
import { isPortalBetaHost } from "../utils/portalHost";
import PortalFeedback from "../components/PortalFeedback";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";

const AUTH_ACTION_TIMEOUT_MS = 25000;

const getSafeRedirect = () => {
  const redirect = new URLSearchParams(window.location.search).get("redirect");
  if (!redirect || !redirect.startsWith("/") || redirect.startsWith("//")) return "";
  return redirect;
};

const getInitialMode = () => {
  const mode = new URLSearchParams(window.location.search).get("mode");
  return ["login", "magic", "forgot", "register"].includes(mode) ? mode : "login";
};

const getInitialEmail = () => {
  const email = new URLSearchParams(window.location.search).get("email");
  return email || "";
};

const shouldEnsureBrokerAccount = (redirect = "") =>
  !redirect.startsWith("/accept-invite");

const audienceOptions = [
  "Aircraft Broker",
  "Flight School",
  "Charter Operator",
  "Fixed Base Operator",
  "Maintenance Facility",
];

function AudienceMultiselect({ selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  return (
    <div ref={dropdownRef} className="w-full">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-left text-white shadow-sm transition hover:bg-white/15 focus:outline-none"
      >
        <span className={selected.length ? "text-white" : "text-white/60"}>
          {selected.length
            ? `${selected.length} selected`
            : "I'm a... Select all that apply"}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-white/75 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="mt-3 w-full overflow-hidden rounded-xl border border-white/20 bg-black/75 text-white shadow-2xl backdrop-blur-xl">
          {audienceOptions.map((option) => {
            const isSelected = selected.includes(option);

            return (
              <label
                key={option}
                className={`flex items-start gap-3 border-b border-white/10 px-4 py-3 text-sm last:border-b-0 ${
                  isSelected ? "bg-red-600/85" : "hover:bg-white/10"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggle(option)}
                  className="mt-0.5 h-4 w-4 accent-red-600"
                />
                <span className="leading-snug">{option}</span>
              </label>
            );
          })}
        </div>
      )}

      {selected.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selected.map((option) => (
            <span
              key={option}
              className="rounded-md border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/85"
            >
              {option}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Login() {
  const [message, setMessage] = useState(null); // new feedback state
  const [mode, setMode] = useState(getInitialMode); // login | magic | forgot | register
  const [email, setEmail] = useState(getInitialEmail);
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [audienceTypes, setAudienceTypes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // eye toggle state
  const confirmed = new URLSearchParams(window.location.search).get("confirmed") === "1";
  const redirectTo = getSafeRedirect();
  const portalBetaHost = isPortalBetaHost();
  const navigate = useNavigate();

  useEffect(() => {
  setMessage(null);
  setError(null);
}, [mode]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError(null);
  setMessage(null); // clear previous message

  try {
    if (mode === "login") {
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        AUTH_ACTION_TIMEOUT_MS,
        "Login request timed out"
      );
      if (error) throw error;

      const initialAccess = await getUserAccess();
      if (
        data?.user?.id &&
        shouldEnsureBrokerAccount(redirectTo) &&
        !initialAccess.isAdmin &&
        !initialAccess.isBroker
      ) {
        const { error: accountError } = await withTimeout(
          supabase.rpc("ensure_broker_account_for_user", {
            target_user_id: data.user.id,
          }),
          AUTH_ACTION_TIMEOUT_MS,
          "Broker account setup timed out"
        );
        if (accountError) throw accountError;
      }

      const access = await getUserAccess();
      navigate(redirectTo || defaultRouteForAccess(access));
    } else if (mode === "magic") {
  const { error } = await withTimeout(
    supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`,
        shouldCreateUser: false, //  only allow existing users to get a magic link
      },
    }),
    AUTH_ACTION_TIMEOUT_MS,
    "Magic link request timed out"
  );
  if (error) {
    // Most common case here will be "user not found"
    // You can parse error.message if you want, but a clear UX line is fine:
    setError("No account found for this email. Please register to access.");
    setLoading(false);
    return; 
  }
    setMessage("Magic link sent! Check your inbox.");
    
    } else if (mode === "forgot") {
      const { error } = await withTimeout(
        supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/update-password`,
        }),
        AUTH_ACTION_TIMEOUT_MS,
        "Password reset request timed out"
      );
      if (error) throw error;
      setMessage("Reset link sent. Check your inbox.");
    } else if (mode === "register") {
      const { error } = await withTimeout(
        supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`,
            data: {
              first_name: firstName,
              last_name: lastName,
              ...(!redirectTo ? { audience_types: audienceTypes } : {}),
            },
          },
        }),
        AUTH_ACTION_TIMEOUT_MS,
        "Registration request timed out"
      );
      if (error) throw error;
      setMessage("Confirmation email sent. Check your inbox.");
    }
  } catch (err) {
    setError(
      isTimeoutError(err)
        ? "This request took too long. Refresh the page and try again."
        : err.message
    );
  }

  setLoading(false);
};

  const toggleAudienceType = (option) => {
    setAudienceTypes((current) =>
      current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option]
    );
  };


  const renderFields = () => (
    <>
      {mode === "register" && (
        <>
          <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-4 py-2 bg-white/10 border border-white/30 text-white rounded-md placeholder-white/60 focus:outline-none"
            required
          />
          <input
            type="text"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-4 py-2 bg-white/10 border border-white/30 text-white rounded-md placeholder-white/60 focus:outline-none"
            required
          />
          {!redirectTo && (
            <AudienceMultiselect
              selected={audienceTypes}
              onToggle={toggleAudienceType}
            />
          )}
        </>
      )}
  
      <input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full px-4 py-2 bg-white/10 border border-white/30 text-white rounded-md placeholder-white/60 focus:outline-none"
        required
      />
  
      {(mode === "login" || mode === "register") && (
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder={mode === "register" ? "Create password" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 pr-10 bg-white/10 border border-white/30 text-white rounded-md placeholder-white/60 focus:outline-none"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      )}
    </>
  );
  

  const titles = {
    login: "Portal Login",
    magic: "Send One-Time Login Link",
    forgot: "Reset Password",
    register: "Create Account",
  };

  const buttonLabels = {
    login: loading ? "Logging In..." : "Log In",
    magic: loading ? "Sending Link..." : "Send Login Link",
    forgot: loading ? "Sending Reset..." : "Send Reset Link",
    register: loading ? "Registering..." : "Register",
  };

  const modeCopy = {
    magic: "Enter the email you have on file to receive a one-time login link.",
    forgot: "Enter the email you have on file and we’ll send a password reset link.",
    register: redirectTo ? "Create your account with the invited email to join the team." : "",
  };

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      <img
        src={loginImage}
        alt="Login Background"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />

      {!portalBetaHost && (
        <div className="md:hidden fixed top-0 left-0 w-full z-50">
          <TopNavBar isMenuOpen={isMenuOpen} toggleMenu={() => setIsMenuOpen(!isMenuOpen)} />
        </div>
      )}



      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-lg bg-white/10 backdrop-blur-xl text-white rounded-xl shadow-2xl border border-white/20 p-10">
          <div className="flex flex-col items-center justify-center text-center mb-6">
          <Link to={portalBetaHost ? "/login" : "/"} onClick={() => trackClickEvent("Login Page Logo Click")}>
  <img src={strobeLogoMono} alt="Strobe Logo" className="w-36 mb-4 hover:opacity-90 transition" />
</Link>

            <h2 className="text-3xl font-bold">{titles[mode]}</h2>
            {modeCopy[mode] && (
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
                {modeCopy[mode]}
              </p>
            )}
          </div>

  <form onSubmit={handleSubmit} className="space-y-4">
	    {confirmed && !message && !error && (
	    <PortalFeedback type="success" variant="dark">
	      Email confirmed. You can sign in now.
	    </PortalFeedback>
	  )}
            {renderFields()}
	            {message && (
	              <PortalFeedback type="success" variant="dark">
	                {message}
	              </PortalFeedback>
	            )}
	            {error && <PortalFeedback type="error" variant="dark">{error}</PortalFeedback>}
            <button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 rounded-md transition"
              disabled={loading}
            >
              {buttonLabels[mode]}
            </button>
          </form>

          {/* 🔄 Mode Switcher */}
          {mode !== "login" && (
            <div className="text-sm mt-6 text-white/70 text-center">
              <button disabled={loading} onClick={() => setMode("login")} className="underline disabled:opacity-60">
                ← Back to login
              </button>
            </div>
          )}

{mode === "login" && (
  <>
    <div className="text-sm mt-4 text-white/70 text-center">
      Don’t remember your password?{" "}
      <button onClick={() => setMode("magic")} className="underline">
        Send a magic link
      </button>
    </div>

    <div className="flex justify-between items-center text-sm mt-2 text-white/70">
      <button onClick={() => setMode("forgot")} className="hover:underline text-left">
        Reset Password
      </button>
      <button onClick={() => setMode("register")} className="hover:underline text-right">
        → Register
      </button>
    </div>
  </>
)}
        </div>
      </div>
    </div>
  );
}
